/**
 * Bundle Builder — full-width grids + bottom-anchored step dock.
 *
 * Tap a product → go to its product page (?bundle_step=N), pick a size, add to
 * cart, and you're returned to the builder (bb-pdp-redirect saves the choice).
 * The anchored bottom dock shows the steps (Tops · Bottoms · Footwear · Look),
 * your picks, the live look price, and the "view your look in cart" CTA.
 */
(function () {
  'use strict';
  if (customElements.get('bundle-builder')) return;

  class BundleBuilder extends HTMLElement {

    constructor() {
      super();
      this.state = {step: 1, top: null, bottom: null, footwear: null};
    }

    connectedCallback() {
      this.discountPct = parseInt(this.dataset.discount || '25', 10) / 100;
      this.footwearPct = parseInt(this.dataset.footwearDiscount || '15', 10) / 100;
      this.hasFootwear = this.dataset.hasFootwear === 'true';
      this.reviewStep  = this.hasFootwear ? 4 : 3;
      this.moneyFmt    = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';

      this.addEventListener('click', this);
      sessionStorage.setItem('bb_return_url', window.location.pathname + window.location.search);
      // Each look gets a stable id; pieces added from the builder carry it so the
      // discount stays anchored to this look. "Start a new look" mints a fresh one.
      if (!sessionStorage.getItem('bb_look_id')) sessionStorage.setItem('bb_look_id', this._genLookId());

      this._restore();
      this._renderDock();
    }

    _genLookId() {
      return 'L' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }

    /* ═══════════════════ Events ═══════════════════ */

    handleEvent(e) {
      // Swatch → that color's variant PDP
      var swatch = e.target.closest('.product-card-swatch');
      if (swatch) {
        e.preventDefault();
        var sc = e.target.closest('.bb-card');
        var sStep = sc && sc.closest('.bb-panel') ? +sc.closest('.bb-panel').dataset.panel : 1;
        var href = swatch.dataset.href;
        if (href) { this.state.step = sStep; this._save(); window.location.href = href + (href.indexOf('?') >= 0 ? '&' : '?') + 'bundle_step=' + sStep; }
        return;
      }

      var dockStep = e.target.closest('.bb-dock-step');
      if (dockStep) { e.preventDefault(); return this._onNav(+dockStep.dataset.step); }

      var skip = e.target.closest('[data-bb-skip]');
      if (skip) { e.preventDefault(); return this._goTo(this.reviewStep); }

      var rmFw = e.target.closest('[data-bb-remove-footwear]');
      if (rmFw) { e.preventDefault(); return this._removeFootwear(); }

      var change = e.target.closest('.bb-review-change');
      if (change) { e.preventDefault(); return this._onReviewChange(+change.dataset.step); }

      var openCart = e.target.closest('[data-bb-cta]:not([disabled]), [data-bb-open-cart]');
      if (openCart) { e.preventDefault(); return this._openCartDrawer(); }

      var addFw = e.target.closest('.bb-add-footwear');
      if (addFw) { e.preventDefault(); return this._goTo(3); }

      var reset = e.target.closest('.bb-new-look, [data-bb-reset]');
      if (reset) { e.preventDefault(); return this._clear(); }

      var card = e.target.closest('.bb-card');
      if (card) { e.preventDefault(); return this._onCard(card); }
    }

    /* ═══════════════════ Card → PDP ═══════════════════ */

    _onCard(card) {
      var panel = card.closest('.bb-panel');
      if (!panel) return;
      var step = +panel.dataset.panel;
      var handle = card.dataset.handle;
      var variantId = card.dataset.variantId;
      if (!handle) return;
      var url = '/products/' + handle + '?bundle_step=' + step;
      if (variantId) url += '&variant=' + variantId;
      // Remember the grid we're leaving from, so a plain browser Back returns here.
      this.state.step = step;
      this._save();
      window.location.href = url;
    }

    /* ═══════════════════ Navigation ═══════════════════ */

    _onNav(s) { if (this._canGo(s)) this._goTo(s); }

    _canGo(s) {
      if (s <= 2) return true;
      return !!(this.state.top && this.state.bottom);
    }

    _goTo(step) {
      this.state.step = step;
      this.querySelectorAll('.bb-panel').forEach(function (p) {
        p.classList.toggle('active', +p.dataset.panel === step);
      });
      if (step === 1 && this.state.top) this._highlight(1, this.state.top);
      if (step === 2 && this.state.bottom) this._highlight(2, this.state.bottom);
      if (this.hasFootwear && step === 3 && this.state.footwear) this._highlight(3, this.state.footwear);
      if (step === this.reviewStep) this._renderReview();
      this._renderDock();
      sessionStorage.setItem('bb_step', String(step));
      this.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    _highlight(step, sel) {
      var panel = this.querySelector('.bb-panel[data-panel="' + step + '"]');
      if (!panel || !sel) return;
      panel.querySelectorAll('.bb-card.selected').forEach(function (c) { c.classList.remove('selected'); });
      var match = null;
      if (sel.colorHandle) match = panel.querySelector('.bb-card[data-product-id="' + sel.productId + '"][data-color="' + sel.colorHandle + '"]');
      if (!match) match = panel.querySelector('.bb-card[data-product-id="' + sel.productId + '"]');
      if (match) match.classList.add('selected');
    }

    _onReviewChange(step) {
      // Just go to that step's grid. The old line is replaced atomically by
      // bb-pdp-redirect the instant the new piece is added — so there's no race
      // and no chance of leaving a duplicate in the cart. We keep the current
      // selection until the user actually picks a replacement.
      this._goTo(step);
    }

    _removeFootwear() {
      var f = this.state.footwear;
      var self = this;
      var done = function () {
        self.state.footwear = null;
        self._save();
        self._renderReview();
        self._renderDock();
      };
      // Wait for the cart removal before updating the UI so the two stay in sync.
      if (f && f.variantId != null) {
        this._removeLiveLine(f.variantId, f.cartKey, sessionStorage.getItem('bb_look_id')).then(done, done);
      } else {
        done();
      }
    }

    /* Remove a cart line by RE-RESOLVING its live key from /cart.js. A stored cart
       line key goes stale the moment the bundle discount re-keys the line, so we
       never reuse it: we look the line up fresh by variant id, preferring the line
       that belongs to this look (_look_id), then remove it by its current key. */
    _removeLiveLine(variantId, prevKey, lookId) {
      return fetch('/cart.js', {headers: {'Accept': 'application/json'}})
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = (cart && cart.items) || [];
          var byKey = null, byLook = null, byVariant = null;
          for (var i = 0; i < items.length; i++) {
            var li = items[i];
            if (prevKey && li.key === prevKey) byKey = li;
            if (String(li.variant_id) === String(variantId)) {
              byVariant = li;
              if (lookId && li.properties && li.properties._look_id === lookId) byLook = li;
            }
          }
          var hit = byKey || byLook || byVariant;
          if (!hit) return;
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify({id: hit.key, quantity: 0})
          });
        })
        .then(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); })
        .catch(function (err) { console.error('[BundleBuilder] remove live line failed:', err); });
    }

    /* ═══════════════════ Session ═══════════════════ */

    _save() {
      sessionStorage.setItem('bb_state', JSON.stringify({top: this.state.top, bottom: this.state.bottom, footwear: this.state.footwear}));
      sessionStorage.setItem('bb_step', String(this.state.step));
    }

    _restore() {
      var raw = sessionStorage.getItem('bb_state');
      if (!raw) return;
      try {
        var s = JSON.parse(raw);
        if (s.top) this.state.top = s.top;
        if (s.bottom) this.state.bottom = s.bottom;
        if (s.footwear) this.state.footwear = s.footwear;
        // Prefer the exact step the shopper was on (so a plain Back returns there).
        // bb-pdp-redirect clears bb_step after a successful add, so a completed pick
        // falls through to the derivation below and the builder advances on its own.
        var step = 1;
        var saved = parseInt(sessionStorage.getItem('bb_step'), 10);
        if (!isNaN(saved) && this._canGo(saved)) {
          step = saved;
        } else if (this.state.top && this.state.bottom) {
          step = (this.hasFootwear && !this.state.footwear) ? 3 : this.reviewStep;
        } else if (this.state.top) {
          step = 2;
        }
        if (step > 1) {
          this._goTo(step);
        } else {
          // Staying on the Tops grid: re-highlight the chosen top without the
          // smooth-scroll that _goTo would trigger on first paint.
          this.state.step = 1;
          if (this.state.top) this._highlight(1, this.state.top);
        }
      } catch (e) { console.error('[BundleBuilder] restore error:', e); }
    }

    _clear() {
      sessionStorage.removeItem('bb_state');
      sessionStorage.removeItem('bb_step');
      // New look group so the next build never merges with the one already in cart.
      sessionStorage.setItem('bb_look_id', this._genLookId());
      this.state = {step: 1, top: null, bottom: null, footwear: null};
      this.querySelectorAll('.bb-card.selected').forEach(function (c) { c.classList.remove('selected'); });
      this._goTo(1);
    }

    /* ═══════════════════ Bottom dock ═══════════════════ */

    _renderDock() {
      var dock = this.querySelector('[data-bb-dock]');
      if (!dock) return;
      var self = this;
      var hasSet = !!(this.state.top && this.state.bottom);
      var setPctN = Math.round(this.discountPct * 100);
      var fwPctN = Math.round(this.footwearPct * 100);

      // Step pills: active = current, done = piece chosen
      dock.querySelectorAll('.bb-dock-step').forEach(function (el) {
        var n = +el.dataset.step;
        var done = (n === 1 && self.state.top) || (n === 2 && self.state.bottom) || (self.hasFootwear && n === 3 && self.state.footwear);
        el.classList.toggle('active', n === self.state.step);
        el.classList.toggle('done', !!done);
        el.disabled = !self._canGo(n);
      });

      // Progress rail (set completion)
      var rail = dock.querySelector('[data-bb-rail]');
      if (rail) {
        var done2 = (this.state.top ? 1 : 0) + (this.state.bottom ? 1 : 0);
        rail.style.width = Math.round((done2 / 2) * 100) + '%';
      }

      // Summary: bigger thumbs + look price + you save
      var sum = dock.querySelector('[data-bb-summary]');
      if (sum) {
        var thumbs = [this._thumb(this.state.top, 'Top'), this._thumb(this.state.bottom, 'Bottom')];
        if (this.hasFootwear) thumbs.push(this._thumb(this.state.footwear, 'Shoes'));
        var priceHtml = '';
        if (hasSet) {
          var p = this._prices();
          priceHtml = '<span class="bb-dock-pricewrap">' +
            '<span class="bb-dock-price">' + this._money(p.lookTotal) + '</span>' +
            (p.savings > 0 ? '<span class="bb-dock-save">save ' + this._money(p.savings) + '</span>' : '') +
          '</span>';
        }
        sum.innerHTML = '<span class="bb-dock-thumbs">' + thumbs.join('') + '</span>' + priceHtml;
      }

      // Discount message
      var msg = dock.querySelector('[data-bb-msg]');
      if (msg) {
        var text, cls = '';
        if (!this.state.top && !this.state.bottom) text = 'Pick a top and a bottom to unlock −' + setPctN + '%';
        else if (!hasSet) text = 'Add a ' + (this.state.top ? 'bottom' : 'top') + ' to unlock −' + setPctN + '%';
        else { text = '−' + setPctN + '% unlocked' + (this.hasFootwear && !this.state.footwear ? ' · add footwear for −' + fwPctN + '%' : ''); cls = 'done'; }
        msg.textContent = text;
        msg.className = 'bb-dock-msg' + (cls ? ' ' + cls : '');
      }

      // CTA
      var cta = dock.querySelector('[data-bb-cta]');
      if (cta) {
        cta.disabled = !hasSet;
        cta.textContent = hasSet ? 'View your look in cart' : 'Choose a top & bottom';
      }
    }

    _thumb(item, label) {
      if (item && item.image) return '<span class="bb-dock-thumb" style="background-image:url(' + item.image + ')"></span>';
      return '<span class="bb-dock-thumb empty">' + this._esc(label || '') + '</span>';
    }

    /* ═══════════════════ Review ═══════════════════ */

    _prices() {
      var t = this.state.top, b = this.state.bottom, f = this.state.footwear;
      var setSale = (t ? t.price : 0) + (b ? b.price : 0);
      var setLook = Math.round(setSale * (1 - this.discountPct));
      var fwSale = f ? f.price : 0;
      var fwLook = f ? Math.round(f.price * (1 - this.footwearPct)) : 0;
      var origTotal = (t ? t.cap : 0) + (b ? b.cap : 0) + (f ? f.cap : 0);
      var saleTotal = setSale + fwSale;
      var lookTotal = setLook + fwLook;
      return {origTotal: origTotal, saleTotal: saleTotal, lookTotal: lookTotal, savings: saleTotal - lookTotal};
    }

    _renderReview() {
      var el = this.querySelector('[data-bb-review]');
      if (!el || !this.state.top || !this.state.bottom) return;
      var t = this.state.top, b = this.state.bottom, f = this.state.footwear;
      var p = this._prices();
      var setPctN = Math.round(this.discountPct * 100);
      var fwPctN = Math.round(this.footwearPct * 100);
      var hasSale = p.origTotal > p.saleTotal;

      var cards = this._reviewCard(t, 1) + this._reviewCard(b, 2);
      if (f) cards += this._reviewCard(f, 3);

      var lookLabel = f ? 'Look price' : 'Look price (-' + setPctN + '%)';
      var lookNote = f ? '<div class="bb-breakdown-note">top &amp; bottom -' + setPctN + '% · footwear -' + fwPctN + '%</div>' : '';

      var breakdown;
      if (hasSale) {
        breakdown =
          '<div class="bb-breakdown-row original"><span>Original price</span><span>' + this._money(p.origTotal) + '</span></div>' +
          '<div class="bb-breakdown-row sale"><span>Sale price</span><span>' + this._money(p.saleTotal) + '</span></div>' +
          '<div class="bb-breakdown-row look"><span>' + lookLabel + '</span><span>' + this._money(p.lookTotal) + '</span></div>' + lookNote +
          '<div class="bb-breakdown-row savings"><span>You save</span><span>-' + this._money(p.savings) + '</span></div>';
      } else {
        breakdown =
          '<div class="bb-breakdown-row original"><span>Price</span><span>' + this._money(p.saleTotal) + '</span></div>' +
          '<div class="bb-breakdown-row look"><span>' + lookLabel + '</span><span>' + this._money(p.lookTotal) + '</span></div>' + lookNote +
          '<div class="bb-breakdown-row savings"><span>You save</span><span>-' + this._money(p.savings) + '</span></div>';
      }

      var addFw = (this.hasFootwear && !f) ? '<button class="bb-add-footwear">+ Add footwear (-' + fwPctN + '%)</button>' : '';

      el.innerHTML =
        '<div class="bb-review-look">' + cards + '</div>' +
        '<div class="bb-breakdown">' + breakdown + '</div>' +
        '<button class="bb-atc" data-bb-open-cart>View your look in cart</button>' +
        addFw +
        '<button class="bb-new-look">Start a new look</button>';
    }

    _reviewCard(item, step) {
      var labels = {1: 'top', 2: 'bottom', 3: 'footwear'};
      var label = labels[step] || '';
      var colorLine = item.colorName ? '<p class="bb-review-item-color">' + this._esc(item.colorName) + '</p>' : '';
      var priceHtml;
      if (item.cap && item.cap > item.price) {
        priceHtml = '<div class="bb-review-item-prices"><span class="bb-review-compare">' + this._money(item.cap) + '</span><span class="bb-review-sale">' + this._money(item.price) + '</span></div>';
      } else {
        priceHtml = '<p class="bb-review-item-price">' + this._money(item.price) + '</p>';
      }
      var actions = '<button class="bb-review-change" data-step="' + step + '">Change ' + label + '</button>';
      if (step === 3) actions += '<button class="bb-review-remove" data-bb-remove-footwear>Remove</button>';
      return '' +
        '<div class="bb-review-item">' +
          '<div class="bb-review-item-img"><img src="' + (item.image || '') + '" alt="' + this._esc(item.title) + '" loading="lazy"></div>' +
          '<p class="bb-review-item-name">' + this._esc(item.title) + '</p>' + colorLine +
          '<p class="bb-review-item-variant">Size: ' + this._esc(item.sizeName) + '</p>' + priceHtml +
          '<div class="bb-review-actions">' + actions + '</div>' +
        '</div>';
    }

    /* ═══════════════════ Cart ═══════════════════ */

    _openCartDrawer() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(function () {
        var drawer = document.getElementById('Cart-Drawer');
        if (drawer) drawer.classList.add('active');
        document.body.classList.add('open-cart', 'open-cc');
      }, 300);
    }

    /* ═══════════════════ Helpers ═══════════════════ */

    _money(cents) {
      var raw = (cents / 100).toFixed(2);
      var parts = raw.split('.');
      var wDot = parts[0].replace(/\B(?=(\d{3})+$)/g, '.');
      var comma = wDot + ',' + parts[1];
      var out = this.moneyFmt
        .replace('{{amount}}', raw)
        .replace('{{amount_no_decimals}}', parts[0])
        .replace('{{amount_with_comma_separator}}', comma)
        .replace('{{amount_no_decimals_with_comma_separator}}', wDot)
        .replace('{{amount_with_apostrophe_separator}}', (wDot + ',' + parts[1]).replace(/\./g, "'"));
      if (out === this.moneyFmt) out = comma + ' €';
      return out;
    }

    _esc(s) {
      var d = document.createElement('span');
      d.textContent = s == null ? '' : String(s);
      return d.innerHTML;
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
